/* eslint-disable no-nested-ternary */
/* eslint-disable react/require-default-props */
import * as React from 'react';
import { Form, Formik } from 'formik';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Appointment, LoadedUser } from '../../interfaces';
import './form.css';
import { initFormValues, checkoutSchema } from '../../utils/form/checkoutForm';
import PricingTable from './PricingTable';
import { formatDollarAmount } from '../../utils/global/helper';
import {
  closeCheckout as closeCheckoutSuccess,
  closeOrderCheckout,
  invalidateAppointment,
} from '../../services/appointments';
import PaymentMethod from './PaymentMethod';
import useCalendarSelectedAppointment from '../../hooks/appointments/useCalendarSelectedAppointment';
import { AppContext } from '../../context/AppContext/context';
import useCheckoutForm from '../../hooks/global/useCheckoutForm';
import ClientSearchAutofill from './ClientSearchAutofill';
import useUser from '../../hooks/global/useUser';
import { addCustomerAlgolia } from '../../services/user';
import useLoadingModal from '../../hooks/global/useLoadingModal';
import useConfirm from '../../hooks/useConfirm';
import {
  CheckoutFormValues,
  CheckoutPaymentMethod,
} from '../../types/appointments';
import CheckoutDetails from './CheckoutDetails';
import useFeatureGuard from '../../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../../types/subscriptions';
import useStripeOptions from '../../hooks/stripe/useStripeOptions';
import { STRIPE_PK } from '../../constants/stripe';
import useGetDiscounts from '../../hooks/discounts/useGetDiscounts';
import {
  initiatePayment,
  invalidateCreateSplitPaymentsGroup,
  invalidateGroupedPayments,
} from '../../services/payments';
import {
  getInitiatePaymentPayload,
  isTipDisplayed,
} from '../../helpers/payments';
import {
  InitiatePaymentResponse,
  SplitPaymentOnFinishArgs,
} from '../../types/payments';
import useCheckoutFormState from '../../hooks/checkouts/useCheckoutFormState';
import { CheckoutFormProvider } from '../../context/CheckoutFormContext';
import useCheckoutPagination from '../../hooks/checkouts/useCheckoutPagination';
import useSelectedSplitPaymentsGroup from '../../hooks/checkouts/useSelectedSplitPaymentsGroup';

const stripePromise = loadStripe(STRIPE_PK as string);

function CheckoutFormComponent() {
  const stripe = useStripe();
  const elements = useElements();

  const {
    setTerminalCheckoutModalToLoading: setTerminalCheckoutLoading,
    openCheckout,
    closeCheckout,
  } = useCheckoutForm();
  const setModalOpen = (open: boolean) =>
    open ? openCheckout() : closeCheckout();
  const confirm = useConfirm();
  const {
    state: {
      isFetching: isCheckoutFetching,
      isError: isErrorCheckout,
      isDebouncing: isDebouncingValues,
    },
  } = useCheckoutFormState();
  const isFetchingCheckout = isCheckoutFetching || isDebouncingValues;

  const { state } = React.useContext(AppContext);
  const { checkout } = state;
  const { user: loadedUser } = useUser<LoadedUser>();
  const { selectedAppointment, setSelectedAppointmentUpdated } =
    useCalendarSelectedAppointment();
  const { data: discounts = [] } = useGetDiscounts({
    activeOnly: true,
  });
  const { updateLoadingModal, closeLoadingModal } = useLoadingModal();

  const { setSelectedSplitPaymentsGroupId } = useSelectedSplitPaymentsGroup();
  const { resetCheckoutFailureData, setMostRecentPaymentId } =
    useCheckoutForm();

  const [formError, setFormError] = React.useState<any>('');
  const [totalDue, setTotalDue] = React.useState<number>();
  const [isTotalCoveredByGiftCard, setIsTotalCoveredByGiftCard] =
    React.useState(false);
  const [isSplitPayment, setIsSplitPayment] = React.useState(false);
  const [appointment, setAppointment] = React.useState<Appointment | null>(
    selectedAppointment,
  );

  const canCheckout = useFeatureGuard(Feature.CHECKOUT);

  const { set: setPaymentsPage } = useCheckoutPagination();
  const resetCheckoutPage = async () => {
    try {
      setPaymentsPage(0);
      setTimeout(() => invalidateGroupedPayments(), 0);
    } catch {
      // do nothing
    }
  };

  const enabledPaymentMethods = React.useMemo(() => {
    if (appointment) {
      const methods: CheckoutPaymentMethod[] = [
        'CREDIT',
        'MANUAL',
        'CASH',
        'FREE',
        'GIFT_CARD',
      ];
      if (appointment.businessId) {
        methods.push('TERMINAL');
      }
      return methods;
    }
    return [
      'TERMINAL',
      'CREDIT',
      'MANUAL',
      'CASH',
      'GIFT_CARD',
    ] as CheckoutPaymentMethod[];
  }, [appointment]);

  const loadedInitValues = React.useMemo<CheckoutFormValues>(() => {
    if (!selectedAppointment)
      return {
        ...initFormValues,
        employeeId:
          loadedUser?.business?.type === 'INDEPENDENT'
            ? loadedUser?.employee?.employeeId
            : '',
      };
    return {
      customer: {
        customerId: selectedAppointment.customerId,
        firstName: selectedAppointment.customer.firstName,
        lastName: selectedAppointment.customer.lastName,
        email: selectedAppointment.customer.email,
        phoneNumber: selectedAppointment.customer.phoneNumber,
        dob: selectedAppointment.customer.dob?.split('T')[0],
      },
      appointmentId: selectedAppointment.appointmentId,
      startDateTime:
        selectedAppointment.startDateTime || new Date().toISOString(),
      price: selectedAppointment.price || 0,
      employeeId: selectedAppointment.employeeId,
      appointmentLengthInMinutes:
        selectedAppointment.appointmentLengthInMinutes || 0,
      tipAmt: initFormValues.tipAmt,
      paymentMethod: initFormValues.paymentMethod,
      includeBonus:
        selectedAppointment.includeEmployeeBonus ?? initFormValues.includeBonus,
      isCashTaxed: true,
    } as CheckoutFormValues;
  }, [
    loadedUser?.business?.type,
    loadedUser?.employee?.employeeId,
    selectedAppointment,
  ]);

  const isZeroAllowed = React.useCallback(
    (values: CheckoutFormValues) =>
      isTotalCoveredByGiftCard ||
      (values.paymentMethod === 'FREE' && !!appointment),
    [isTotalCoveredByGiftCard, appointment],
  );

  const isSubmitDisabled = React.useCallback(
    (values: CheckoutFormValues, isSubmitting: boolean) => {
      if (!values.paymentMethod || !canCheckout || isErrorCheckout) return true;
      if (!values.customer?.phoneNumber || !values.customer?.firstName)
        return true;
      const total = totalDue ?? 0;
      const allowZero = isZeroAllowed(values);
      const isValidTotal = total > 0 || allowZero;
      const isValidPrice =
        !values.appointmentId ||
        (allowZero || values.customOrderItems?.length
          ? values.price >= 0
          : values.price > 0);
      return isSubmitting || !isValidTotal || !isValidPrice;
    },
    [canCheckout, totalDue, isZeroAllowed, isErrorCheckout],
  );

  const getSubmitButtonText = React.useCallback(
    (values: CheckoutFormValues) => {
      if ((totalDue ?? 0) <= 0 && isTotalCoveredByGiftCard)
        return 'Record gift card payment';
      if ((totalDue ?? 0) <= 0 && isZeroAllowed(values))
        return 'Complete appointment';
      const dollarAmount = formatDollarAmount(Math.max(0, totalDue ?? 0));
      if (values.paymentMethod === 'CASH')
        return `Record ${dollarAmount} Payment`;
      if (values.paymentMethod === 'MANUAL') return `Checkout ${dollarAmount}`;
      if (values.paymentMethod === 'GIFT_CARD')
        return 'Record gift card payment';
      return `Request ${dollarAmount}`;
    },
    [isTotalCoveredByGiftCard, isZeroAllowed, totalDue],
  );

  const handleManualPayment = async (
    payload: CheckoutFormValues,
    checkoutResponse: InitiatePaymentResponse,
  ) => {
    const { paymentIntent } = checkoutResponse;
    if (!paymentIntent?.client_secret) {
      return {
        error: 'Failed to initiate payment. Please try again.',
      };
    }

    const submission = await elements?.submit();
    if (submission?.error) {
      return {
        error: submission.error.message,
      };
    }
    // get setup intent from card element rendered in child
    const cardElement = elements?.getElement('card');
    if (!cardElement || !stripe) {
      return {
        error: 'Invalid payment method. Please try again.',
      };
    }

    const result = await stripe.confirmCardPayment(
      paymentIntent?.client_secret,
      {
        payment_method: {
          card: cardElement,
        },
        save_payment_method: false,
        setup_future_usage: 'off_session',
        receipt_email: payload.customer?.email || undefined,
      },
    );
    if (result.error) {
      return {
        error: result.error.message,
      };
    }

    // Close checkout
    if (result.paymentIntent?.status === 'succeeded') {
      if (appointment) {
        const totalAmountDue = +(totalDue || 0).toFixed(2);
        const tipAmt = payload.tipAmt || 0;
        const tattooPrice = Math.max(0, totalAmountDue - tipAmt);
        await closeCheckoutSuccess(appointment.appointmentId, {
          tattooPrice: Number(tattooPrice.toFixed(2)),
          tipAmt: Number(tipAmt.toFixed(2)),
          paymentId: checkoutResponse.paymentIntent?.paymentId || undefined,
        });
      } else if (checkoutResponse.order?.orderId) {
        await closeOrderCheckout(checkoutResponse.order.orderId);
        // webhook may take a few seconds to update the order payments/transfers
        await new Promise(resolve => {
          setTimeout(resolve, 2000);
        });
      }
    }

    return {
      result,
    };
  };

  const onSplitPayment = async (values: CheckoutFormValues) => {
    if (
      Number(values.tipAmt || 0) > 0 &&
      isTipDisplayed({
        values,
        appointment,
        user: loadedUser,
      })
    ) {
      const accepted = await confirm({
        title: 'Tip will be removed',
        message:
          'The tip you entered will be removed from the total due. Tips can only be accepted through Terminal devices for split payments.',
      });
      if (!accepted) return;
    }

    await invalidateCreateSplitPaymentsGroup();
    setIsSplitPayment(true);
  };

  const onComplete = async ({
    customerId,
    splitPaymentsGroupId,
  }: SplitPaymentOnFinishArgs) => {
    resetCheckoutFailureData();

    if (selectedAppointment) {
      setSelectedAppointmentUpdated(true);
    }

    if (customerId) {
      addCustomerAlgolia(customerId, loadedUser?.business.id || '');
    }

    if (appointment?.appointmentId) {
      await invalidateAppointment(appointment?.appointmentId);
    }

    if (splitPaymentsGroupId) {
      setSelectedSplitPaymentsGroupId(splitPaymentsGroupId);
    }

    setIsSplitPayment(false);
    setModalOpen(false);

    await resetCheckoutPage();
  };

  return (
    <Box
      style={{
        width: 900,
        margin: 'auto',
      }}>
      <Formik
        enableReinitialize
        initialValues={loadedInitValues}
        validationSchema={checkoutSchema}
        onSubmit={async (values, { setSubmitting, setValues }) => {
          if (!canCheckout || isFetchingCheckout || isErrorCheckout) return;
          if (
            (totalDue ?? 0) <= 0 &&
            isZeroAllowed(values) &&
            !isTotalCoveredByGiftCard
          ) {
            const accepted = await confirm({
              title: 'Free Checkout',
              message:
                'Once you check this appointment out, you will not be able to go back and edit the price to collect a payment. Are you sure you want to continue?',
            });
            if (!accepted) return;
          }

          const header =
            values.paymentMethod === 'MANUAL'
              ? 'Processing...'
              : 'Initiating Checkout...';
          updateLoadingModal({
            header,
            message:
              'This could take a minute. Please do not refresh the page.',
            status: 'loading',
          });

          setSubmitting(true);
          if (!values.customer?.phoneNumber || !values.customer?.firstName) {
            setFormError('Client information is required');
            setSubmitting(false);
            return;
          }

          const selectedDiscount = values.discountId
            ? discounts.find(discount => discount.id === values.discountId)
            : undefined;

          let closeModal = true;
          try {
            const { data: checkoutPayload, overridePaymentMethod } =
              getInitiatePaymentPayload({
                appointmentId: appointment?.appointmentId,
                businessId: appointment?.businessId || loadedUser?.business?.id,
                values,
                discount: selectedDiscount,
                failureData: checkout.checkoutFailureData,
              });
            const checkoutResponse = await initiatePayment(
              checkoutPayload,
              values.customer,
            );
            const paymentMethod = overridePaymentMethod || values.paymentMethod;

            if (paymentMethod === 'MANUAL') {
              const paymentResult = await handleManualPayment(
                values,
                checkoutResponse,
              );
              if (paymentResult.error) {
                closeModal = false;
                if (checkoutResponse?.order?.orderId) {
                  setValues(prev => ({
                    ...prev,
                    failedOrderId: checkoutResponse.order.orderId,
                    paymentIntentId: checkoutResponse.paymentIntent?.id,
                  }));
                }
                toast.error(paymentResult.error);
                closeLoadingModal();
                return;
              }
            }

            if (checkoutResponse) {
              if (paymentMethod === 'CREDIT') {
                toast.success('Checkout link sent to customer!');
              }
              if (paymentMethod === 'TERMINAL') {
                setTerminalCheckoutLoading();
                toast.success('Charge sent to terminal');
              }
              if (paymentMethod === 'CASH') {
                toast.success('Payment recorded');
              }
              if (paymentMethod === 'GIFT_CARD') {
                toast.success('Gift card payment recorded');
              }
              if (checkoutResponse.paymentIntent?.paymentId) {
                setMostRecentPaymentId(
                  checkoutResponse.paymentIntent?.paymentId,
                );
              }
            }

            await onComplete({
              customerId: checkoutResponse?.customerId,
            });
          } catch (error: any) {
            if (error?.response?.data?.message) {
              setFormError(error?.response?.data?.message);
            } else {
              setFormError(
                'Error initiating checkout. Please make sure all fields are filled in and try again.',
              );
            }
            toast.error('Error initiating checkout');
          } finally {
            setSubmitting(false);
            if (closeModal) closeLoadingModal();
          }
        }}>
        {({ isSubmitting, setFieldValue, values, isValid }) => (
          <>
            <ClientSearchAutofill
              title={selectedAppointment ? 'Client & Appointment' : undefined}
              hideBirthday
              namePrefix={['customer']}
              selectedRequest={appointment ?? undefined}
              allowCustomerWithoutAppointment
            />
            <Form
              style={{
                paddingTop: 0,
              }}>
              {formError && (
                <Typography variant="body1" color="primary">
                  {formError}
                </Typography>
              )}
              <CheckoutDetails
                appointment={appointment}
                onSelectAppointment={setAppointment}
              />
              <PricingTable
                appointment={appointment}
                setFieldValue={setFieldValue}
                values={values}
                setTotalDue={setTotalDue}
                setIsTotalCoveredByGiftCard={setIsTotalCoveredByGiftCard}
                totalDue={totalDue}
                isSplitPayment={isSplitPayment}
                onCloseSplitPayment={() => setIsSplitPayment(false)}
                onCheckoutSuccess={onComplete}
              />
              <PaymentMethod
                enabledPaymentMethods={enabledPaymentMethods}
                selectedAppointment={appointment}
                totalDue={totalDue || 0}
                onSplitPayment={() => onSplitPayment(values)}
              />
              <Button
                type="submit"
                disabled={!isValid || isSubmitDisabled(values, isSubmitting)}
                variant="contained"
                className="primary">
                {getSubmitButtonText(values)}
              </Button>
            </Form>
          </>
        )}
      </Formik>
    </Box>
  );
}

export default function CheckoutForm() {
  const stripeOptions = useStripeOptions();
  return (
    <CheckoutFormProvider>
      <Elements stripe={stripePromise} options={stripeOptions}>
        <CheckoutFormComponent />
      </Elements>
    </CheckoutFormProvider>
  );
}
